import { Type } from '@sinclair/typebox';
import { priceSchema } from "../price.mjs";
import { cartAddressSchema, cartCustomerSchema } from "./customer.mjs";
export const paymentMethodsWithOwnBuyButton = { adyen: ['paypal', 'applepay'] };
export const basePaymentMethodSchema = Type.Object({
    id: Type.String(),
    name: Type.String(),
    price: priceSchema,
    logoUrls: Type.Array(Type.String()),
    /**
     * This is the id of an element that must be present in the DOM when the payment method is
     * rendered. The SDK will render into the element for dynamic payment methods like Adyen.
     */
    elementId: Type.String(),
});
export const adyenPaymentProvider = Type.Literal('adyen');
export const adyenPaymentMethodSchema = Type.Intersect([
    basePaymentMethodSchema,
    Type.Object({
        provider: adyenPaymentProvider,
        clientKey: Type.String(),
        environment: Type.Union([Type.Literal('test'), Type.Literal('live')]),
        adyenCssFileUrl: Type.String(),
        adyenPaymentMethods: Type.Array(Type.Unknown()), // We don't own the type so we don't have a proper type for it
        adyenPaymentMethodConfigurations: Type.Record(Type.String(), Type.Unknown()), // This is data that is just passed through to the Adyen SDK so we don't need to know the structure
    }),
]);
export const stripePaymentProvider = Type.Literal('stripe');
export const paymentMethodSchema = Type.Union([adyenPaymentMethodSchema]);
export const paymentProviderSchema = Type.Union([adyenPaymentProvider, stripePaymentProvider]);
export const paymentProvidersWithOurCompletePurchaseSchema = Type.Union([adyenPaymentProvider]);
/**
 * In lack of a better word, this represents a payment that is done through using the StickerApp
 * checkout page to select payment method and pay for the order, and not using a redirect to a
 * third-party payment provider or showing a custom checkout page like with Klarna Checkout.
 */
export const checkoutPagePaymentSchema = Type.Object({
    $type: Type.Literal('checkoutPage'),
    paymentMethods: Type.Array(paymentMethodSchema),
    selectedPaymentMethod: Type.Optional(Type.Object({ id: Type.String(), provider: paymentProvidersWithOurCompletePurchaseSchema, name: Type.String() })),
});
export const stripeCheckoutPaymentSchema = Type.Object({
    $type: Type.Literal('stripeCheckout'),
    apiKey: Type.String(),
    elements: Type.Object({
        paymentIntentClientSecret: Type.String(),
        status: Type.Union([Type.Object({ $type: Type.Literal('open') }), Type.Object({ $type: Type.Literal('complete'), orderId: Type.String() })]),
    }),
});
export const freeCheckoutPaymentSchema = Type.Object({
    /**
     * This represents when the cost of the cart is covered by other payments such as gift cards or
     * when the cart consists only of free items.
     */
    $type: Type.Literal('free'),
});
export const alreadyPaidPaymentSchema = Type.Object({
    /**
     * This is what you get if you try to load the checkout data with a cart that has already been
     * paid for. The SDK will detect this state and then clear the cart and show the order confirmation
     * page. The checkout page should either be blank or show a loading spinner when this type is detected
     * and wait for the `onShowOrderConfirmation` callback to be called.
     */
    $type: Type.Literal('alreadyPaid'),
    orderId: Type.String(),
});
export const stripeCheckoutPaymentMetaDataSchema = Type.Object({
    provider: stripePaymentProvider,
    /**
     * This is used for express purchases where we get customer information as part
     * of the payment process. In this case we don't want to create a new version when
     * getting the customer data because the customer paid for the version of the cart
     * that they saw. So we store it here to signify that we got the customer data
     * as part of the payment process.
     */
    paymentCustomer: Type.Optional(cartCustomerSchema),
    elements: Type.Object({
        paymentIntentId: Type.String(),
        paymentIntentClientSecret: Type.String(),
        paymentIntentCreatedForCartVersion: Type.String(),
    }),
});
export const checkoutPaymentMetaDataSchema = Type.Union([stripeCheckoutPaymentMetaDataSchema]);
export const cartPaymentSchema = Type.Union([
    checkoutPagePaymentSchema,
    stripeCheckoutPaymentSchema,
    freeCheckoutPaymentSchema,
    alreadyPaidPaymentSchema,
]);
export const paymentMethodLogosResponseSchema = Type.Object({
    logoUrls: Type.Array(Type.String()),
});
export const savePaymentMetadataCustomerRequestSchema = Type.Object({
    address: Type.Union([Type.Null(), cartAddressSchema]),
    billingAddress: Type.Union([Type.Null(), cartAddressSchema]),
    email: Type.Union([Type.Null(), Type.String()]),
    phoneNumber: Type.Union([Type.Null(), Type.String()]),
});
//# sourceMappingURL=payment.js.map